// TODO(ui-foundation): promote this component to ui-foundation and remove from this repo.
import { useCallback, useEffect, useRef, useState } from "react";
import { eventsAdminUserServiceClient } from "@app/api/user-service-client";
import { AuthLinkButton } from "@app/components/atoms/auth-link-button";
import { AuthHeading } from "@app/components/molecules/auth-heading";
import { mapUserServiceError } from "@app/lib/auth/user-service-errors";
import { Button, PinInput } from "@app/lib/ui-foundation";

const RESEND_COOLDOWN_SECONDS = 30;

export type VerifyEmailFormProps = {
  email: string;
  initialStatusMessage?: string;
  onConfirmed: () => void;
  onStartOver: () => void;
  headingAs?: "h1" | "h2";
};

export function VerifyEmailForm({
  email,
  initialStatusMessage = "",
  onConfirmed,
  onStartOver,
  headingAs = "h2",
}: VerifyEmailFormProps) {
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState(initialStatusMessage);
  const [cooldown, setCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const handle = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(handle);
  }, [cooldown]);

  const submitCode = useCallback(
    async (candidate: string) => {
      if (candidate.length !== 6 || verifyingRef.current) {
        return;
      }
      verifyingRef.current = true;
      setIsVerifying(true);
      setErrorMessage("");
      setStatusMessage("");
      try {
        try {
          await eventsAdminUserServiceClient.confirmSignup({
            email,
            confirmation_code: candidate,
          });
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error("[verify-email] confirm failed", error);
          }
          setCode("");
          setErrorMessage(mapUserServiceError(error).message);
          return;
        }
        onConfirmed();
      } finally {
        verifyingRef.current = false;
        setIsVerifying(false);
      }
    },
    [email, onConfirmed, verifyingRef],
  );

  const handleCodeChange = useCallback(
    (next: string) => {
      const cleaned = next.replace(/\D/g, "").slice(0, 6);
      setCode(cleaned);
      if (cleaned.length === 6) {
        void submitCode(cleaned);
      }
    },
    [submitCode],
  );

  const handleResend = useCallback(async () => {
    if (cooldown > 0 || isResending) {
      return;
    }
    setErrorMessage("");
    setStatusMessage("");
    setIsResending(true);
    try {
      await eventsAdminUserServiceClient.resendConfirmation({ email });
      setStatusMessage("We sent a new code to your email.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("[verify-email] resend failed", error);
      }
      setErrorMessage(mapUserServiceError(error).message);
    } finally {
      setIsResending(false);
    }
  }, [cooldown, email, isResending]);

  const resendLabel =
    cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code";

  return (
    <>
      <AuthHeading
        as={headingAs}
        title="Verify your email"
        subtitle={
          <>
            Enter the 6-digit code we sent to <strong>{email}</strong>.
          </>
        }
      />

      <form
        className="events-form-stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCode(code);
        }}
      >
        <div className="events-mfa-code events-mfa-code--verify">
          <PinInput size="md" disabled={isVerifying}>
            <PinInput.Group
              maxLength={6}
              value={code}
              onChange={handleCodeChange}
              autoComplete="one-time-code"
              inputMode="numeric"
              aria-label="Verification code"
            >
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <PinInput.Slot key={index} index={index} />
              ))}
            </PinInput.Group>
          </PinInput>
        </div>

        <p
          className="events-auth-card__notice"
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </p>
        <p className="events-auth-card__error" role="alert" aria-live="polite">
          {errorMessage}
        </p>

        <Button
          size="lg"
          color="primary"
          className="events-solid-button"
          type="submit"
          isDisabled={code.length !== 6 || isVerifying}
        >
          {isVerifying ? "Verifying…" : "Verify email"}
        </Button>
      </form>

      <div className="events-auth-card__footer events-auth-card__footer--inline">
        <AuthLinkButton
          onClick={handleResend}
          disabled={cooldown > 0 || isResending}
        >
          {resendLabel}
        </AuthLinkButton>
      </div>

      <div className="events-auth-card__footer">
        Wrong email?{" "}
        <AuthLinkButton onClick={onStartOver}>Start over</AuthLinkButton>
      </div>
    </>
  );
}
